import React, { useState, useEffect } from "react";
import {
  Header,
} from "../components/Header";
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarToday from '@mui/icons-material/CalendarToday';
import ClassIcon from '@mui/icons-material/Class';

import { PieChart } from "@mui/x-charts/PieChart";
import { getAttendance } from "../services/getAttendance";
import { getCourses } from "../services/getCourses";
import useUserStore from "../store/UserStore";
import useSelectionsStore from "../store/UserSelectionsStore.js";

import { STPSelect } from "../components/Select";
import { CustomCalendar } from "../components/CustomCalendar";
import { useTranslation } from "react-i18next";
import { UsuariListButton } from '../components/UsuariListButton.jsx';
import { saveUserAction } from "../services/saveUserAction.js";

export function Assistència({ setIsAuthenticated }) {
  const { t } = useTranslation();
  const user = useUserStore((state) => state.user);
  const [attendanceData, setAttendanceData] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const selections = useSelectionsStore((state) => state.selections);
  const setSelections = useSelectionsStore((state) => state.setSelections);
  const [showYearSelection, setShowYearSelection] = useState(!selections.codeje);
  const [showCourseSelection, setShowCourseSelection] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelections({})
  }, [])

  useEffect(() => {
    getCourses()
      .then((response) => {
        const orderedCourses = response.data.sort((a, b) => b.curso.codeje - a.curso.codeje).reverse();
        setCourses(orderedCourses);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching courses:", error);
        setLoading(false);
      });
  }, [user.idpersona]);

  useEffect(() => {
    setAttendanceData([]);
    if (selectedCourse && selections.codeje) {
      getAttendance(selectedCourse)
        .then((response) => {
          setAttendanceData(response.data);
        })
        .catch((error) => {
          console.error("Error fetching attendance:", error);
        });
    }
  }, [user.idpersona, selectedCourse, selections.codeje]);

  const handleCodejeSelect = (codeje) => {
    setSelections({
      ...selections,
      codeje: codeje,
    });
    setShowYearSelection(false);
    setShowCourseSelection(true);
  };

  const handleCourseSelect = (courseId) => {
    setSelectedCourse(courseId);
    setSelections({
      ...selections,
      course: courseId,
    });
    setShowCourseSelection(false);
    saveUserAction({ id_accion: 2, detalles_accion: `Accessed to the attendance of the course ${courseId}` });
  };

  const handleGoBack = () => {
    setSelectedCourse("");
    setShowCourseSelection(true);
  };

  const handleGoBackToYears = () => {
    setShowYearSelection(true);
    setShowCourseSelection(false);
    setSelections({ ...selections, codeje: null });
  };

  const getPieChartData = () => {
    const counts = attendanceData.reduce((acc, curr) => {
      acc[curr.valor] = (acc[curr.valor] || 0) + 1;
      return acc;
    }, {});
    return [
      { id: 0, value: counts[1] || 0, label: t("attendance.attendance"), color: "#50a579" },
      { id: 1, value: counts[2] || 0, label: t("attendance.delay"), color: "#e28b48" },
      { id: 2, value: counts[3] || 0, label: t("attendance.absence"), color: "#df7868" },
      { id: 3, value: counts[4] || 0, label: t("attendance.onlineAttendance"), color: "#507cba" },
    ];
  };

  let codeje_courses = [];
  let filteredCourses = [];

  courses.forEach((course) => {
    if (!codeje_courses.includes(course.curso.codeje)) {
      codeje_courses.push(course.curso.codeje);
    }

    if (selections.codeje && course.curso.codeje === selections.codeje) {
      filteredCourses.push(course);
    }
  });

  return (
    loading ? 
    (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ 
        color: 'rgba(80, 165, 121, 0.7)',
       }}/>
    </Box>) :

    <Box sx={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      width: "100%",
    }}>
      <Header setIsAuthenticated={setIsAuthenticated} />

      {showYearSelection ? (
        <Box sx={{ flexGrow: 1, padding: 3, width: "100%", margin: "auto" }}>
          <Typography variant="h4" sx={{ mb: 6, color: "black", textAlign: "center" }}>
            {t("userSelection.selectUser")} <span style={{ color: "#50A579" }}>{t("attendance.year")}</span>
          </Typography>
          <List sx={{ width: "100%", maxWidth: 540 }}>
            {codeje_courses.map((codeje) => (
              <ListItem key={codeje} disablePadding>
                <UsuariListButton onClick={() => handleCodejeSelect(codeje)}>
                  <ListItemIcon>
                    <CalendarToday sx={{ color: "#50A579" }} />
                  </ListItemIcon>
                  <ListItemText primary={codeje} />
                </UsuariListButton>
              </ListItem>
            ))}
          </List>
        </Box>
      ) : showCourseSelection ? (
        <Box sx={{ flexGrow: 1, padding: 3, width: "100%", margin: "auto" }}>
          <Typography variant="h4" sx={{ mb: 3, color: "black", textAlign: "center" }}>
            {t("userSelection.selectUser")} <span style={{ color: "#50A579" }}>{t("attendance.course")}</span>
          </Typography>

          <IconButton
            aria-label="back"
            onClick={handleGoBackToYears}
            sx={{ mb: 3 }}
          >
            <ArrowBackIcon /> <Typography sx={{ ml: 2 }} variant="body1">{t("attendance.back")}</Typography>
          </IconButton> {/* Flecha para volver a la selección de año */}

          <List sx={{ width: "100%", maxWidth: 540 }}>
            {filteredCourses.map((course) => (
              <ListItem key={course.idcurso} disablePadding>
                <UsuariListButton onClick={() => handleCourseSelect(course.idcurso)}>
                  <ListItemIcon>
                    <ClassIcon sx={{ color: "#50A579" }} />
                  </ListItemIcon>
                  <ListItemText primary={course.curso ? course.curso.descripcion : course.idcurso} />
                </UsuariListButton>
              </ListItem>
            ))}
          </List>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 4, color: "black", textAlign: "center" }}>
            <strong>{t("attendance.title")}</strong>
          </Typography>

          {/* Botón para volver atrás */}
          <Box sx={{ mb: 3, display: "flex", flexDirection: "row", alignItems: "center" }}>
            <IconButton
              aria-label="back"
              onClick={handleGoBack}
            >
              <ArrowBackIcon /> <Typography sx={{ ml: 2 }} variant="body1">{t("attendance.selectAnotherCourse")}</Typography>
            </IconButton>
          </Box>

          <hr />

          <Typography variant="h6" sx={{ mb: 2, color: "black", textAlign: "center" }}>
            {courses.find((c) => c.idcurso === selectedCourse)?.curso?.descripcion || ""}
          </Typography>

          <hr />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {attendanceData.length > 0 ? (
                <CustomCalendar attendanceData={attendanceData} />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                  <CircularProgress sx={{ 
                    color: 'rgba(80, 165, 121, 0.7)',
                  }}/>
                </Box>
              )}

            </Grid>
            <Grid item xs={12} md={6}>
              {attendanceData.length > 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <PieChart sx={{ ml: 7 }}
                    series={[
                      {
                        data: getPieChartData(),
                        highlightScope: { faded: "global", highlighted: "item" },
                        faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                      },
                    ]}
                    width={300}
                    height={200}
                    legend={{ hidden: true }}
                  />
                  <Box sx={{ ml: 2 }}>
                    {getPieChartData().map((item) => (
                      <Box key={item.id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Box
                          sx={{
                            minWidth: 16,
                            aspectRatio: "1/1",
                            borderRadius: "50%",
                            backgroundColor: item.color,
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2">
                          {item.label}: {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body1" align="center">
                  {t("attendance.selectCourseToViewStats")}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}